/**
 * A mansard that slopes its room's ceiling (`src/state/roomCeiling.ts`), drawn as that ceiling rather than as a wedge:
 * its own cells — its slope and the tops of its niches — and its steps, the vertical faces at a free end and at a niche's
 * sides. The side facing the room takes its «Скат» finish; the other, which the orbit sees from above, its «Верх». Where a
 * window lies in it the slope has a hole, lined to the window at the lining's outer end (`ROOF_WINDOW_LINING`), with the
 * sky beyond. The wall behind stays as high as the ceiling lets it (`wallTop`), so nothing stands behind the slope.
 *
 * The cells are worked out where they are on the plan; the mesh is put into the item's own frame, so the viewer places it
 * as it places any item.
 */
import * as THREE from 'three';
import type { Item, Vec2 } from '../../core/hp/types';
import { ceilingEdges, stepOwner, type CeilingCell, type CeilingSlope, type RoomCeiling } from '../../state/roomCeiling';
import { withoutHoles } from '../../core/geometry/stairwells';
import { materialFor } from './buildScene';
import type { TextureImageCache } from './TextureImageCache';

/** How deep the lining round a window lying in a slope is: about a roof's build-up, from the ceiling to the window. */
export const ROOF_WINDOW_LINING = .18;

type P3 = [number, number, number];

/** Where a slope's own coordinates — across its width, into the room — put a point of the plan. */
const planPoint = (slope: CeilingSlope, a: number, n: number): Vec2 => ({
  x: slope.frame.origin.x + slope.frame.right.x * a + slope.frame.inward.x * n,
  z: slope.frame.origin.z + slope.frame.right.z * a + slope.frame.inward.z * n,
});

const signedArea = (polygon: readonly Vec2[]) => polygon.reduce((sum, p, i) => { const q = polygon[(i + 1) % polygon.length]; return sum + p.x * q.z - q.x * p.z; }, 0) / 2;
const counterClockwise = (polygon: Vec2[]) => signedArea(polygon) < 0 ? polygon.reverse() : polygon;

/** The holes the slope's roof windows make in it, on the plan, counter-clockwise. */
export function roofWindowHoles(slope: CeilingSlope): Vec2[][] {
  return slope.roofWindows.map(cut => {
    const n0 = cut.y0 / slope.rise, n1 = cut.y1 / slope.rise;
    return counterClockwise([planPoint(slope, cut.a0, n0), planPoint(slope, cut.a1, n0), planPoint(slope, cut.a1, n1), planPoint(slope, cut.a0, n1)]);
  });
}

/** Whether a cell is under this slope: its slope or the top of one of its niches. */
const ownCell = (cell: CeilingCell, index: number) => cell.surface.kind !== 'flat' && cell.surface.slope === index;

/**
 * The faces of the slope, as triangles where they are: `inner` facing the room, `outer` the same turned away. UVs run in
 * metres across the slope and up it.
 */
export function slopeTriangles(ceiling: RoomCeiling, index: number): { inner: Array<{ points: [P3, P3, P3]; uv: [number, number, number, number, number, number] }> } {
  const slope = ceiling.slopes[index], inner: Array<{ points: [P3, P3, P3]; uv: [number, number, number, number, number, number] }> = [];
  const stretch = Math.hypot(1, slope.rise);
  const local = (p: Vec2) => { const d = { x: p.x - slope.frame.origin.x, z: p.z - slope.frame.origin.z };
    return { a: d.x * slope.frame.right.x + d.z * slope.frame.right.z, n: d.x * slope.frame.inward.x + d.z * slope.frame.inward.z }; };
  /** A triangle facing `toward` (a direction in the world). */
  const push = (points: [P3, P3, P3], uv: [number, number, number, number, number, number], toward: P3) => {
    const [a, b, c] = points, e1 = [b[0] - a[0], b[1] - a[1], b[2] - a[2]], e2 = [c[0] - a[0], c[1] - a[1], c[2] - a[2]];
    const normal = [e1[1] * e2[2] - e1[2] * e2[1], e1[2] * e2[0] - e1[0] * e2[2], e1[0] * e2[1] - e1[1] * e2[0]];
    if (Math.hypot(normal[0], normal[1], normal[2]) < 1e-12) return;
    if (normal[0] * toward[0] + normal[1] * toward[1] + normal[2] * toward[2] < 0) inner.push({ points: [a, c, b], uv: [uv[0], uv[1], uv[4], uv[5], uv[2], uv[3]] });
    else inner.push({ points, uv });
  };
  const holes = roofWindowHoles(slope);
  // Its cells, less the windows lying in it: convex pieces, fanned.
  for (const cell of ceiling.cells) {
    if (!ownCell(cell, index)) continue;
    const height = (p: Vec2) => cell.base + cell.gx * p.x + cell.gz * p.z, flat = cell.surface.kind === 'raise';
    for (const piece of cell.surface.kind === 'slope' ? withoutHoles(cell.polygon, holes) : [cell.polygon]) {
      const points = piece.map(p => [p.x, height(p), p.z] as P3);
      const uvs = piece.map(p => { const { a, n } = local(p); return [a, flat ? n : n * stretch] as const; });
      for (let i = 1; i + 1 < points.length; i++)
        push([points[0], points[i], points[i + 1]], [uvs[0][0], uvs[0][1], uvs[i][0], uvs[i][1], uvs[i + 1][0], uvs[i + 1][1]], [0, -1, 0]);
    }
  }
  // Its steps: a free end, a niche's sides — vertical, facing the side where the ceiling is higher.
  for (const edge of ceilingEdges(ceiling)) {
    if (!edge.step || stepOwner(edge) !== index) continue;
    const { from, to } = edge, length = Math.hypot(to.x - from.x, to.z - from.z);
    // The cell that told the edge lies on its left; its right is the other side.
    const right: P3 = [(to.z - from.z) / length, 0, -(to.x - from.x) / length];
    const otherHigher = edge.heights[0][1] + edge.heights[1][1] > edge.heights[0][0] + edge.heights[1][0];
    const toward: P3 = otherHigher ? right : [-right[0], 0, -right[2]];
    const low0 = Math.min(...edge.heights[0]), high0 = Math.max(...edge.heights[0]), low1 = Math.min(...edge.heights[1]), high1 = Math.max(...edge.heights[1]);
    const a: P3 = [from.x, low0, from.z], b: P3 = [to.x, low1, to.z], c: P3 = [to.x, high1, to.z], d: P3 = [from.x, high0, from.z];
    push([a, b, c], [0, low0, length, low1, length, high1], toward);
    push([a, c, d], [0, low0, length, high1, 0, high0], toward);
  }
  // The linings round its windows: from the hole's edge out of the room, facing into the hole.
  const out: P3 = [-slope.rise * slope.frame.inward.x / stretch, 1 / stretch, -slope.rise * slope.frame.inward.z / stretch];
  for (const hole of holes) {
    const middle = hole.reduce((sum, p) => ({ x: sum.x + p.x / hole.length, z: sum.z + p.z / hole.length }), { x: 0, z: 0 });
    const lift = (p: Vec2): P3 => { const y = slope.frame.knee + slope.rise * local(p).n; return [p.x + out[0] * ROOF_WINDOW_LINING, y + out[1] * ROOF_WINDOW_LINING, p.z + out[2] * ROOF_WINDOW_LINING]; };
    const on = (p: Vec2): P3 => [p.x, slope.frame.knee + slope.rise * local(p).n, p.z];
    let run = 0;
    hole.forEach((p, i) => {
      const q = hole[(i + 1) % hole.length];
      const edgeMiddle = { x: (p.x + q.x) / 2, z: (p.z + q.z) / 2 }, toward: P3 = [middle.x - edgeMiddle.x, 0, middle.z - edgeMiddle.z];
      // Toward the hole's middle, along the slope: the in-plan direction lifted onto the slope.
      const lifted: P3 = [toward[0], slope.rise * (toward[0] * slope.frame.inward.x + toward[2] * slope.frame.inward.z), toward[2]];
      const span = Math.hypot(...on(q).map((v, k) => v - on(p)[k]) as P3);
      push([on(p), on(q), lift(q)], [run, 0, run + span, 0, run + span, ROOF_WINDOW_LINING], lifted);
      push([on(p), lift(q), lift(p)], [run, 0, run + span, ROOF_WINDOW_LINING, run, ROOF_WINDOW_LINING], lifted);
      run += span;
    });
  }
  return { inner };
}

/**
 * The mansard's node: its faces in its room's ceiling, the room side in its «Скат» material and the other side in its
 * «Верх» one, in the item's own frame. `real` for the resize and the plan's footprint, `generated` so it is disposed of
 * rather than given back to the model library.
 */
export function slopeNode(item: Item, ceiling: RoomCeiling, index: number, options: { maxAnisotropy: number; textureImages?: TextureImageCache }): THREE.Mesh {
  const { inner } = slopeTriangles(ceiling, index);
  // The item's frame: the viewer stands it at its position and elevation, turned by −rotation about the vertical.
  const toLocal = new THREE.Matrix4().compose(new THREE.Vector3(item.position.x, item.elevation, item.position.z),
    new THREE.Quaternion().setFromEuler(new THREE.Euler(0, -item.rotation, 0)), new THREE.Vector3(1, 1, 1)).invert();
  const position: number[] = [], uv: number[] = [], v = new THREE.Vector3();
  const add = (points: readonly P3[], uvs: readonly number[]) => {
    for (const point of points) { v.set(point[0], point[1], point[2]).applyMatrix4(toLocal); position.push(v.x, v.y, v.z); }
    uv.push(...uvs);
  };
  for (const { points, uv: coords } of inner) add(points, coords);
  const innerCount = position.length / 3;
  // The same faces turned away, for the side the room does not see.
  for (const { points: [a, b, c], uv: [u0, v0, u1, v1, u2, v2] } of inner) add([a, c, b], [u0, v0, u2, v2, u1, v1]);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(position, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  geometry.addGroup(0, innerCount, 0);
  geometry.addGroup(innerCount, position.length / 3 - innerCount, 1);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox(); geometry.computeBoundingSphere();
  const materials = (['slope', 'top'] as const).map(slot => {
    const material = materialFor(item.materials?.[slot] ?? item.materials?.['*'], { maxAnisotropy: options.maxAnisotropy, textureImages: options.textureImages,
      fallbackColor: slot === 'slope' ? 0xf6f5f3 : 0xe7e4df });
    material.name = slot;
    // It faces down into the room as the ceiling does, and takes the ceiling's bounce surrogate (buildScene).
    if (slot === 'slope') { material.emissive.copy(material.color); material.emissiveIntensity = .22; }
    return material;
  });
  const mesh = new THREE.Mesh(geometry, materials);
  mesh.castShadow = true; mesh.receiveShadow = true;
  mesh.position.set(item.position.x, item.elevation, item.position.z);
  mesh.rotation.y = -item.rotation;
  mesh.name = `item:${item.id}`;
  mesh.userData = { kind: 'item', itemId: item.id, caption: item.caption, model: item.modelRef.raw, real: true, generated: true, slope: true };
  return mesh;
}

/** What makes a slope's node look as it does: its own faces, holes and steps, and its finishes. */
export function slopeSignature(item: Item, ceiling: RoomCeiling, index: number): string {
  const round = (value: number) => Math.round(value * 1e4) / 1e4;
  const own = ceiling.cells.filter(cell => ownCell(cell, index)).map(cell => [cell.polygon.map(p => [round(p.x), round(p.z)]), round(cell.base), round(cell.gx), round(cell.gz)]);
  const steps = ceilingEdges(ceiling).filter(edge => edge.step).map(edge => [round(edge.from.x), round(edge.from.z), round(edge.to.x), round(edge.to.z), edge.heights.flat().map(round)]);
  return JSON.stringify([own, steps, roofWindowHoles(ceiling.slopes[index]).map(hole => hole.map(p => [round(p.x), round(p.z)])), item.materials ?? null,
    round(item.position.x), round(item.position.z), round(item.elevation), round(item.rotation)]);
}
